import { useState, type DragEvent } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { useToast } from '@/shared/contexts/ToastContext'

import { AttachmentAddButtons } from './AttachmentAddButtons'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import { noteSx, textActionSx } from './attachmentStyles'
import { AttachmentTile, SendingTile } from './AttachmentTiles'
import { useAttachmentPicker } from './useAttachmentPicker'
import { useAttachmentSending } from './useAttachmentSending'
import { useRecordingImages } from './useRecordingImages'

interface Props {
  yakurekiId: string
  // 題 (「添付資料」)
  title: string
  // 足せない理由。渡すとタイルの列の代わりにこの 1 行だけを出す (出す・出さない・文言は呼び出し側が決める)
  blockedReason?: string
}

// 作る前の資料の panel (管理栄養士の名前の行の下。メモと横並び): 題「添付資料」・96px のタイル一覧・その下に
// 足す口の icon 2 つ ([📷 撮る][⬆ 選ぶ])。削除は 28 spec のアンチパターン (重要操作を hover に隠さない) を避け、サムネ右上に常時可視の
// ✕ で置く。資料は再添付できるため確認ダイアログは挟まない (MVP)。panel はドロップも受ける。
// 足す口・送信中と送れなかった資料・一覧の失敗の扱いは一覧 (AttachmentList) と同じ部品
// (design-book 02-components/attachments-panel.md「AttachmentsPanel」)。種別は持たない (ADR-0054)。
export const AttachmentsPanel = ({
  yakurekiId,
  title,
  blockedReason,
}: Props) => {
  const { showToast } = useToast()
  const { list, remove } = useRecordingImages(yakurekiId)
  const { sending, add, retry, save, reason, setReason } =
    useAttachmentSending(yakurekiId)
  const picker = useAttachmentPicker({
    onBlob: (blob) => add(blob),
    onUnreadable: setReason,
  })
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const images = list.data ?? []

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // 子の要素へ移っただけのときは消さない
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
    setDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    setReason(null)
    void picker.addFiles(Array.from(e.dataTransfer.files))
  }

  // 題の行 (メモの panel の題と同じ字格)
  const heading = (
    <Typography
      variant="subtitle2"
      component="h2"
      sx={{ mb: 1.5, fontWeight: 600 }}
    >
      {title}
    </Typography>
  )

  if (blockedReason) {
    return (
      <Box>
        {heading}
        <Typography sx={noteSx}>{blockedReason}</Typography>
      </Box>
    )
  }

  const handleDelete = (imageId: string) => {
    remove.mutate(imageId, {
      onError: () => showToast('資料を削除できませんでした', 'error'),
    })
  }

  const preview = images.find((img) => img.id === previewId) ?? null
  const hasTiles = images.length > 0 || sending.length > 0

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        borderRadius: `${RADIUS.md}px`,
        bgcolor: dragging ? colorTokens.bg.muted : 'transparent',
        transition: 'background-color 120ms',
      }}
    >
      {heading}
      {list.isError && (
        <Typography sx={{ ...noteSx, mb: 1 }}>
          資料の一覧を読み込めませんでした{' '}
          <Box
            component="button"
            type="button"
            onClick={() => void list.refetch()}
            sx={textActionSx}
          >
            もう一度
          </Box>
        </Typography>
      )}
      {hasTiles && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          {images.map((img) => (
            <AttachmentTile
              key={img.id}
              image={img}
              alt="添付資料"
              onOpen={() => setPreviewId(img.id)}
            >
              <IconButton
                size="small"
                aria-label="この資料を削除"
                onClick={() => handleDelete(img.id)}
                disabled={remove.isPending}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 22,
                  height: 22,
                  bgcolor: colorTokens.bg.default,
                  border: `1px solid ${colorTokens.border.default}`,
                  color: colorTokens.fg.muted,
                  '&:hover': {
                    bgcolor: colorTokens.bg.muted,
                    color: colorTokens.state.dangerFg,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </AttachmentTile>
          ))}

          {sending.map((item) => (
            <SendingTile
              key={item.localId}
              item={item}
              onRetry={() => void retry(item)}
              onSave={() => save(item)}
            />
          ))}
        </Box>
      )}
      {reason && <Typography sx={{ ...noteSx, mt: 1 }}>{reason}</Typography>}
      {/* 足す口はタイルの下 (一覧と同じ。0 件ならこれだけ) */}
      <Box sx={{ mt: hasTiles || reason ? 1.5 : 0 }}>
        <AttachmentAddButtons
          onCapture={() => {
            setReason(null)
            picker.openCamera()
          }}
          onPick={() => {
            setReason(null)
            picker.openFilePicker()
          }}
        />
      </Box>

      {picker.elements}
      <AttachmentPreviewDialog
        image={preview}
        onClose={() => setPreviewId(null)}
      />
    </Box>
  )
}
